import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import PublicNav from "@/components/PublicNav";
import { MapPin, BadgeCheck, ShieldCheck, Briefcase, Building2, Star, MessageSquare, UserPlus, Store } from "lucide-react";

export default function PublicProfile() {
  const { handle } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [error, setError] = useState(null);
  const [following, setFollowing] = useState(false);
  const [followersCount, setFollowersCount] = useState(0);
  const [actionError, setActionError] = useState("");
  const [listings, setListings] = useState([]);

  const toggleFollow = async () => {
    setActionError("");
    try {
      const res = await api.post(`/users/${profile.nexa_id}/follow`);
      setFollowing(res.data.following);
      setFollowersCount(res.data.followers_count);
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  const contact = async () => {
    setActionError("");
    try {
      const res = await api.post("/conversations", { type: "direct", handles: [profile.nexa_id] });
      navigate(`/messages/${res.data.conversation_id}`);
    } catch (err) {
      setActionError(formatApiError(err));
    }
  };

  useEffect(() => {
    api
      .get(`/profiles/${handle}`)
      .then((r) => {
        setProfile(r.data);
        setFollowing(Boolean(r.data.followed_by_me));
        setFollowersCount(r.data.followers_count ?? 0);
      })
      .catch((err) => setError(err?.response?.status || 500));
    api
      .get("/market/listings", { params: { seller: handle } })
      .then((r) => setListings(r.data))
      .catch(() => {});
  }, [handle]);

  if (error === 401) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div data-testid="profile-members-only" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Ce profil est réservé aux membres NEXA.{" "}
          <Link to="/login" className="text-brand-turquoise font-medium hover:underline">Se connecter</Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div data-testid="profile-not-found" className="max-w-3xl mx-auto px-6 py-24 text-center text-slate-500">
          Profil introuvable.{" "}
          <Link to="/" className="text-brand-turquoise font-medium hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PublicNav />
        <div className="max-w-4xl mx-auto px-6 py-24 flex justify-center">
          <div className="w-10 h-10 border-4 border-slate-200 border-t-brand-turquoise rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div data-testid="public-profile-page" className="min-h-screen bg-slate-50">
      <PublicNav />

      <div className="bg-brand-navy">
        <div className="max-w-4xl mx-auto px-6 py-14">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 rounded-full bg-brand-turquoise/20 border-2 border-brand-turquoise flex items-center justify-center text-brand-turquoise text-2xl font-extrabold shrink-0">
              {profile.name?.[0]?.toUpperCase() || "?"}
            </div>
            <div className="min-w-0">
              <h1 data-testid="profile-name" className="text-2xl sm:text-3xl font-extrabold text-white truncate">{profile.name}</h1>
              <p data-testid="profile-handle" className="text-brand-turquoise font-medium">@{profile.nexa_id}</p>
              <div className="flex flex-wrap items-center gap-3 text-sm text-white/70 mt-1">
                <span className="flex items-center gap-1.5"><Briefcase className="w-4 h-4" />{ROLE_LABELS[profile.role] || profile.role}</span>
                {(profile.city || profile.country) && (
                  <span className="flex items-center gap-1.5"><MapPin className="w-4 h-4" />{[profile.city, profile.country].filter(Boolean).join(", ")}</span>
                )}
                {profile.member_since && (
                  <span>Membre depuis {new Date(profile.member_since).toLocaleDateString("fr-CA", { month: "long", year: "numeric" })}</span>
                )}
                <span data-testid="profile-followers-count" className="flex items-center gap-1.5">
                  {followersCount} abonné(s) · {profile.following_count ?? 0} abonnement(s)
                </span>
              </div>
            </div>
          </div>
          {profile.headline && <p data-testid="profile-headline" className="text-white/90 mt-4 font-medium">{profile.headline}</p>}
          {user && profile.nexa_id && user.nexa_id !== profile.nexa_id && (
            <div className="flex flex-wrap gap-2 mt-4">
              <button
                data-testid="profile-contact-button"
                onClick={contact}
                className="inline-flex items-center gap-2 bg-brand-turquoise text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-white hover:text-brand-navy transition-colors duration-200"
              >
                <MessageSquare className="w-4 h-4" />
                Contacter
              </button>
              <button
                data-testid="profile-follow-button"
                onClick={toggleFollow}
                className="inline-flex items-center gap-2 border border-white/40 text-white text-sm font-semibold rounded-lg px-5 py-2.5 hover:bg-white/10 transition-colors duration-200"
              >
                <UserPlus className="w-4 h-4" />
                {following ? "Abonné — se désabonner" : "Suivre"}
              </button>
            </div>
          )}
          {actionError && (
            <p data-testid="profile-action-error" className="text-xs text-red-400 mt-3">{actionError}</p>
          )}
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {profile.bio && (
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <p data-testid="profile-bio" className="text-slate-600 text-sm leading-relaxed whitespace-pre-line">{profile.bio}</p>
          </div>
        )}

        <div className="grid md:grid-cols-2 gap-6">
          {profile.reputation && (
            <div data-testid="profile-reputation" className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
                <Star className="w-4 h-4 text-brand-orange" />
                Réputation NEXA
              </h2>
              <div className="flex items-center gap-3 mb-4">
                <span data-testid="reputation-score" className="text-3xl font-extrabold text-brand-navy">{profile.reputation.score}</span>
                <span className="text-xs text-slate-400">/ 100 — score transparent et explicable</span>
              </div>
              <ul className="space-y-2">
                {profile.reputation.components.map((c) => (
                  <li key={c.key} data-testid={`reputation-component-${c.key}`} className="flex items-center justify-between text-sm">
                    <span className="text-slate-600">{c.label}</span>
                    <span className="font-semibold text-brand-turquoise">+{c.points}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {profile.verifications && (
            <div data-testid="profile-verifications" className="bg-white rounded-xl border border-slate-200 p-6">
              <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-brand-turquoise" />
                Vérifications
              </h2>
              <div className="flex flex-wrap gap-2">
                {profile.verifications.map((v, i) => (
                  <span
                    key={`${v.type}-${v.subject}-${i}`}
                    data-testid={`verification-badge-${v.type}-${i}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium bg-brand-turquoise/10 text-brand-navy rounded-full px-3 py-1.5"
                  >
                    <BadgeCheck className="w-3.5 h-3.5 text-brand-turquoise" />
                    {v.type === "email" ? "Courriel vérifié" : v.subject}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {profile.cv && (
          <div data-testid="profile-cv" className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-brand-navy mb-3">Profil professionnel</h2>
            <p className="text-sm font-medium text-brand-navy">{profile.cv.profession}</p>
            <p className="text-xs text-slate-500 mt-0.5">
              {profile.cv.experience_years} an(s) d'expérience
              {[profile.cv.city, profile.cv.country].filter(Boolean).length > 0 &&
                ` · ${[profile.cv.city, profile.cv.country].filter(Boolean).join(", ")}`}
            </p>
            {profile.cv.skills?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3">
                {profile.cv.skills.map((s) => (
                  <span key={s} className="text-xs bg-brand-sky text-brand-navy rounded-full px-2.5 py-0.5">{s}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {listings.length > 0 && (
          <div data-testid="profile-marketplace" className="bg-white rounded-xl border border-slate-200 p-6">
            <h2 className="font-semibold text-brand-navy mb-4 flex items-center gap-2">
              <Store className="w-4 h-4 text-brand-turquoise" />
              Boutique NEXA Market
            </h2>
            <div className="grid sm:grid-cols-2 gap-3">
              {listings.map((l) => (
                <Link
                  key={l.listing_id}
                  data-testid={`profile-listing-${l.listing_id}`}
                  to={`/marche/${l.listing_id}`}
                  className="border border-slate-200 rounded-lg p-4 hover:border-brand-turquoise hover:shadow-sm transition-all duration-200"
                >
                  <p className="font-medium text-brand-navy text-sm">{l.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">{l.kind === "produit" ? "Produit" : "Service"}</p>
                  <div className="flex items-center justify-between mt-2">
                    <span className="font-extrabold text-brand-navy text-sm">{l.price} $ {l.currency}</span>
                    {l.rating_count > 0 && (
                      <span className="inline-flex items-center gap-1 text-xs text-slate-500">
                        <Star className="w-3.5 h-3.5 fill-brand-orange text-brand-orange" />
                        {l.rating_avg} ({l.rating_count})
                      </span>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}

        {profile.company && (
          <Link
            data-testid="profile-company-link"
            to={`/entreprises/${profile.company.company_id}`}
            className="flex items-center gap-3 bg-white rounded-xl border border-slate-200 p-5 hover:border-brand-turquoise hover:shadow-md transition-all duration-200"
          >
            <Building2 className="w-5 h-5 text-brand-blue shrink-0" />
            <div>
              <p className="font-semibold text-brand-navy text-sm">{profile.company.name}</p>
              {profile.company.sector && <p className="text-xs text-slate-400">{profile.company.sector}</p>}
            </div>
          </Link>
        )}
      </div>
    </div>
  );
}
